// The schedule — the heart of the product.
//
// Phase 7 stage 3 (docs/phase-7-parity.md). Replaces the phase 2 CalendarPage's
// two views with the seven of spec 05-scheduling.md as amended on 2026-08-19.
// This file is the container only: it owns the anchor, the filters and the
// data, and hands a window of bookings to whichever view is showing. The views
// themselves are in components/schedule and know nothing about loading.
//
// Role gating is NOT done here. The whole bookings router is
// `requireRole('manager')`, so a field worker is refused by the API before any
// of this matters; the route guard in App.tsx only stops them being shown a
// page that would fail.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BOOKABLE_KINDS,
  scheduling,
  type CalendarBooking,
  type Client,
  type JobListItem,
  type NonWorkingDay,
  type Resource,
  type Unavailability,
} from '../schedulingApi';
import { ApiError } from '../api';
import {
  addMonths,
  eachDate,
  monthGrid,
  startOfMonth,
  todayISO,
  type DateOnly,
} from '../lib/dates';
import { BookingModal } from '../components/BookingModal';
import { MaintenanceModal } from '../components/MaintenanceModal';
import { AgendaView } from '../components/schedule/AgendaView';
import { DayPanel } from '../components/schedule/DayPanel';
import { JobDayPanel, type JobDayTarget } from '../components/schedule/JobDayPanel';
import { WeekView } from '../components/schedule/WeekView';
import { MonthView } from '../components/schedule/MonthView';
import { ThreeMonthView } from '../components/schedule/ThreeMonthView';
import { JobTimeline } from '../components/schedule/JobTimeline';
import { ResourceTimeline } from '../components/schedule/ResourceTimeline';
import { ReplanningPanel, type ReplanningItem } from '../components/schedule/ReplanningPanel';
import {
  hasDateNav,
  isResourceTimeline,
  maintenanceOnly,
  MAINTAINABLE_KINDS,
  labelFor,
  rangeFor,
  stepFor,
  VIEWS,
  viewKind,
  type ScheduleView,
} from '../components/schedule/views';
import { SearchableSelect } from '../components/ui';
import { KIND_LABEL } from '../lib/resourceGroups';
import { useMediaQuery } from '../lib/useMediaQuery';
import { makeNwInfo, makeWorksOn } from '../lib/nonWorkingDays';
import type { JobDayGroup } from '../lib/jobGroups';
import { useLatestRequest } from '../lib/useLatestRequest';

interface Filters {
  resourceId: string;
  kind: string;
  category: string;
  clientId: string;
}

const EMPTY_FILTERS: Filters = { resourceId: '', kind: '', category: '', clientId: '' };
const FILTER_KEY = 'dayboard.calendar.filters';
const VIEW_KEY = 'dayboard.schedule.view';

export function SchedulePage() {
  const today = todayISO();

  // The view persists like the filters do. A manager who lives in Crew View
  // should not have to pick it again every morning.
  //
  // First visit only: a phone starts on Agenda, everything else on the month
  // grid. At 375px the grid squeezes every chip to two letters, while Agenda
  // is the day-first list a phone is for; the grid stays the default
  // everywhere else (spec 05, rounds 6 and 11), and any view the person picks
  // is remembered on every screen size alike.
  const [view, setView] = useState<ScheduleView>(() => {
    const stored = window.localStorage.getItem(VIEW_KEY);
    if (VIEWS.some((v) => v.id === stored)) return stored as ScheduleView;
    // Week on desktop since parity pass 3 (round 22) — it is where ZT opens
    // and where a manager actually works; Agenda stays the phone default.
    return window.matchMedia('(max-width: 640px)').matches ? 'agenda' : 'week';
  });
  useEffect(() => {
    window.localStorage.setItem(VIEW_KEY, view);
  }, [view]);

  const [anchor, setAnchor] = useState<DateOnly>(today);

  // Filters persist per user (spec 05). localStorage is the right amount of
  // machinery for a preference nobody would miss if a browser forgot it.
  const [filters, setFilters] = useState<Filters>(() => {
    try {
      const stored = window.localStorage.getItem(FILTER_KEY);
      return stored ? { ...EMPTY_FILTERS, ...(JSON.parse(stored) as Filters) } : EMPTY_FILTERS;
    } catch {
      return EMPTY_FILTERS;
    }
  });
  useEffect(() => {
    window.localStorage.setItem(FILTER_KEY, JSON.stringify(filters));
  }, [filters]);

  const [bookings, setBookings] = useState<CalendarBooking[]>([]);
  const [away, setAway] = useState<Unavailability[]>([]);
  const [nonWorkingWeekdays, setNonWorkingWeekdays] = useState<number[]>([]);
  const [nonWorkingDays, setNonWorkingDays] = useState<NonWorkingDay[]>([]);
  const [replanning, setReplanning] = useState<ReplanningItem[]>([]);
  const [resources, setResources] = useState<Resource[]>([]);
  const [jobs, setJobs] = useState<JobListItem[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [openDay, setOpenDay] = useState<DateOnly | null>(null);
  const [openJobDay, setOpenJobDay] = useState<JobDayTarget | null>(null);
  // Job bookings edit as a LIST (one form, many resources — round 22);
  // maintenance keeps its own single-machine modal.
  const [editingEntries, setEditingEntries] = useState<CalendarBooking[] | null>(null);
  const [editingMaintenance, setEditingMaintenance] = useState<CalendarBooking | null>(null);

  /** Routes a booking to its editor by species. */
  const openEditor = (booking: CalendarBooking) => {
    if (booking.kind === 'maintenance') setEditingMaintenance(booking);
    else setEditingEntries([booking]);
  };
  const [creating, setCreating] = useState<{ date: DateOnly; resourceId?: string; jobId?: string } | null>(null);
  const [bookingMaintenance, setBookingMaintenance] = useState<{
    date: DateOnly;
    resourceId?: string;
  } | null>(null);

  const range = useMemo(() => rangeFor(view, anchor), [view, anchor]);

  // DM5: at phone width the week view renders as the agenda-style day list
  // over the week's range — the ZT mobile plan's rule, "the full grid is
  // reserved for tablets and landscape". The VIEW is unchanged (same range,
  // same ‹ › stepping, same stored choice); only the rendering swaps.
  const phone = useMediaQuery('(max-width: 639.98px)');

  const begin = useLatestRequest();

  const load = useCallback(async () => {
    // Stepping the schedule twice quickly, or typing in a filter, puts two of
    // these in flight; only the newest may write to the page. See
    // `useLatestRequest`.
    const isCurrent = begin();
    setLoading(true);
    setError(null);
    try {
      const [calendar, unavailable, needs, nonWorking] = await Promise.all([
        scheduling.calendar({
          from: range.from,
          to: range.to,
          resourceId: filters.resourceId || undefined,
          kind: filters.kind || undefined,
          category: filters.category || undefined,
          clientId: filters.clientId || undefined,
        }),
        scheduling.unavailabilityIn({
          from: range.from,
          to: range.to,
          resourceId: filters.resourceId || undefined,
        }),
        scheduling.needsReplanning(),
        // Weekdays ride along on every fetch — one request, whole answer.
        scheduling.nonWorkingDays({ from: range.from, to: range.to }),
      ]);
      if (!isCurrent()) return;
      setBookings(calendar.bookings);
      setAway(unavailable.unavailability);
      setReplanning(needs.bookings);
      setNonWorkingWeekdays(nonWorking.weekdays);
      setNonWorkingDays(nonWorking.days);
    } catch (err) {
      if (!isCurrent()) return;
      setError(err instanceof ApiError ? err.message : 'Could not load the schedule.');
    } finally {
      // A superseded load must not turn the spinner off under the one that
      // replaced it.
      if (isCurrent()) setLoading(false);
    }
  }, [begin, range.from, range.to, filters]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void Promise.all([
      // Archived ones too. A machine archived while it still had work keeps
      // its bookings, and both the board and the booking editors have to be
      // able to name it — `timelineResources` below and the modals' "keep the
      // booking's own resource even if archived" clauses were both written
      // for rows that never arrived, so neither could ever fire.
      scheduling.listResources({ includeArchived: true }),
      scheduling.listJobs(),
      scheduling.listClients(),
      scheduling.resourceCategories(),
    ])
      .then(([r, j, c, cat]) => {
        setResources(r.resources);
        setJobs(j.jobs);
        setClients(c.clients);
        setCategories(cat.categories);
      })
      .catch(() => {
        /* the pickers stay empty; the error banner from load() is enough */
      });
  }, []);

  /** Bookings laid out per day, so each cell can be drawn in one pass. */
  const byDay = useMemo(() => {
    const map = new Map<string, CalendarBooking[]>();
    for (const booking of bookings) {
      for (const date of eachDate(booking.startDate, booking.endDate)) {
        const list = map.get(date);
        if (list) list.push(booking);
        else map.set(date, [booking]);
      }
    }
    return map;
  }, [bookings]);

  /** The company-wide shading, built once per load and handed down — the
      views stay data-dumb (round 22). */
  const nwInfo = useMemo(
    () => makeNwInfo(nonWorkingWeekdays, nonWorkingDays),
    [nonWorkingWeekdays, nonWorkingDays]
  );
  const worksOn = useMemo(() => makeWorksOn(nwInfo), [nwInfo]);

  const awayByDay = useMemo(() => {
    const map = new Map<string, Unavailability[]>();
    for (const entry of away) {
      for (const date of eachDate(entry.startDate, entry.endDate)) {
        const list = map.get(date);
        if (list) list.push(entry);
        else map.set(date, [entry]);
      }
    }
    return map;
  }, [away]);

  const days = useMemo(() => eachDate(range.from, range.to), [range.from, range.to]);

  /** Resources with work in the window on screen. */
  const bookedIds = useMemo(() => new Set(bookings.map((b) => b.resourceId)), [bookings]);

  /**
   * What the Resource filter offers: everything still in service, plus any
   * archived one with work in the window.
   *
   * The same rule the board draws by. Offering to filter to a machine the
   * board cannot show would be a dead end, and listing every machine ever
   * retired would bury the ones in use.
   */
  const selectableResources = useMemo(
    () => resources.filter((r) => r.status !== 'archived' || bookedIds.has(r.id)),
    [resources, bookedIds]
  );

  /**
   * Which resources get a row on a timeline board.
   *
   * Archived ones are dropped unless they still have work in the window — an
   * archived machine with bookings on it is exactly the situation someone needs
   * to see and sort out, and hiding the row would hide the problem.
   */
  const timelineResources = useMemo(() => {
    const kind = viewKind(view);
    return resources
      // The Maintenance board shows everything that can go to a mechanic, not
      // one kind — a yard's plant and its vehicles are one question.
      .filter((r) =>
        maintenanceOnly(view) ? MAINTAINABLE_KINDS.includes(r.kind) : kind ? r.kind === kind : true
      )
      .filter((r) => (filters.resourceId ? r.id === filters.resourceId : true))
      .filter((r) => (filters.category ? r.category === filters.category : true))
      .filter((r) => r.status !== 'archived' || bookedIds.has(r.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [resources, view, bookedIds, filters.resourceId, filters.category]);

  /**
   * What the board draws. On Maintenance, job bookings are filtered out.
   *
   * That view answers one question — what is off the road, and who has it —
   * and the job work on those same machines is the answer to a different one.
   * Showing both would bury it.
   */
  const visibleBookings = useMemo(
    () => (maintenanceOnly(view) ? bookings.filter((b) => b.kind === 'maintenance') : bookings),
    [bookings, view]
  );

  /**
   * "Sort it out" opens the flagged booking's editor and MOVES NOTHING.
   *
   * The panel lists every flagged booking, company-wide and unbounded above,
   * while the board holds one window — so this used to look the booking up in
   * the window on screen, and, not finding it (most of the time, on Agenda or
   * Week), set the anchor to the booking's own dates and opened it once that
   * window had loaded. Two things were wrong with that. Pressing a button on a
   * warning panel silently threw the board to another month, with no way back
   * but leaving the page, which is what Nathan hit: the board moved, and the
   * second press moved it again. And the errand could simply never finish —
   * with a kind or category filter on, the booking never arrives in the
   * window, so the board stayed moved, nothing opened, and the pending id sat
   * there waiting to pop a modal open at some unrelated later load.
   *
   * The panel's own rows are full calendar bookings now (the server sends the
   * same enriched shape), so there is nothing to look up and nowhere to go.
   */
  function onSaved() {
    setEditingEntries(null);
    setEditingMaintenance(null);
    setCreating(null);
    void load();
  }

  /**
   * A job tile is several bookings, so it opens the per-job panel; a
   * maintenance group is exactly one booking wearing a supplier's name, so
   * it goes straight to its editor — a panel listing one row would be a
   * detour dressed up as a step.
   */
  const openGroup = (group: JobDayGroup, date: DateOnly) => {
    if (group.jobId === null) {
      const booking = group.bookings[0];
      if (booking) openEditor(booking);
      return;
    }
    setOpenJobDay({ jobId: group.jobId, date });
  };

  const removeBooking = async (booking: CalendarBooking) => {
    const label = booking.jobTitle ?? 'this booking';
    if (!window.confirm(`Remove ${booking.resourceName} from ${label}?`)) return;
    try {
      const result = await scheduling.releaseBooking(booking.id);
      if (result.outcome === 'truncated') {
        window.alert(
          `That booking had already started, so the days already worked stay on the record. ` +
            `It now ends on ${result.newEndDate}.`
        );
      }
      void load();
    } catch (err) {
      window.alert(err instanceof ApiError ? err.message : 'Could not release that booking.');
    }
  };

  const filtersActive = JSON.stringify(filters) !== JSON.stringify(EMPTY_FILTERS);

  return (
    <>
      {/* Toolbar — Zanetto Tools' shape (parity pass 3): a title row with the
          action buttons on the right, then a CENTRED column holding the view
          tabs as one joined segmented control and, for the anchored views, the
          date navigation. The action buttons' emphasis is fixed regardless of
          view — ZT tried swapping primary between them and recorded it as a
          mis-click hazard. */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="!mb-0 text-2xl font-bold text-gray-900">Schedule</h1>
        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
          {/* Two buttons rather than one with a dropdown: they make different
              things, the forms have almost nothing in common, and a manager
              knows which one they came here for. */}
          <Link to="/app/calendar/non-working-days" className="btn-secondary btn-sm">
            Non-working days
          </Link>
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() => setBookingMaintenance({ date: today })}
          >
            Book maintenance
          </button>
          <button
            type="button"
            className="btn-primary btn-sm"
            onClick={() => setCreating({ date: today })}
          >
            New booking
          </button>
        </div>
      </div>

      <div className="mb-5 flex flex-col items-center gap-2">
        {/* One JOINED control, horizontally scrollable on phones, rather than
            the old wrapping pill group: nine tabs wrapped into a ragged block
            at phone width, and a strip that scrolls reads as one control. */}
        <div className="max-w-full overflow-x-auto">
          <div
            className="flex w-max overflow-hidden rounded-lg border border-gray-300 shadow-sm"
            role="group"
            aria-label="Schedule view"
          >
            {VIEWS.map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => setView(v.id)}
                aria-pressed={view === v.id}
                className={`min-h-[44px] border-r border-gray-300 px-3 py-2.5 text-sm font-medium transition-colors last:border-r-0 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-brand-600 sm:min-h-0 sm:py-1.5 ${
                  view === v.id
                    ? 'bg-brand-500 text-ink'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>
        </div>

        {hasDateNav(view) && (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              className="btn-secondary btn-sm !px-2"
              // Functional form, and it is not style. `stepFor(view, anchor, …)`
              // closes over the anchor from the render that bound the handler,
              // so two clicks landing before React re-renders both compute the
              // same destination and the second does nothing — stepping twice
              // quickly moved the board one week. Found in the browser,
              // 2026-09-02.
              onClick={() => setAnchor((from) => stepFor(view, from, -1))}
              aria-label="Previous"
            >
              ‹
            </button>
            <button type="button" className="btn-secondary btn-sm" onClick={() => setAnchor(today)}>
              Today
            </button>
            <button
              type="button"
              className="btn-secondary btn-sm !px-2"
              onClick={() => setAnchor((from) => stepFor(view, from, 1))}
              aria-label="Next"
            >
              ›
            </button>
            <span className="ml-1 text-sm font-semibold text-gray-700">
              {labelFor(view, anchor)}
            </span>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <div className="w-52">
          <label className="form-label" htmlFor="filter-resource">
            Resource
          </label>
          <SearchableSelect
            id="filter-resource"
            options={selectableResources.map((r) => ({
              value: r.id,
              label: r.name,
              note: r.category ?? undefined,
            }))}
            value={filters.resourceId}
            onChange={(resourceId) => setFilters({ ...filters, resourceId })}
            placeholder="All, type to search…"
          />
        </div>

        <div>
          <label className="form-label" htmlFor="filter-kind">
            Kind
          </label>
          <select
            id="filter-kind"
            className="form-select"
            value={filters.kind}
            onChange={(e) => setFilters({ ...filters, kind: e.target.value })}
          >
            <option value="">All</option>
            {/* Driven off the shared kind list rather than hard-coded, so this
                filter cannot fall behind the six kinds the way it fell behind
                at batch 1. Suppliers are absent because they are never on a
                board — nothing is booked against them. */}
            {BOOKABLE_KINDS.map((k) => (
              <option key={k} value={k}>
                {KIND_LABEL[k]}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="form-label" htmlFor="filter-category">
            Category
          </label>
          <select
            id="filter-category"
            className="form-select"
            value={filters.category}
            onChange={(e) => setFilters({ ...filters, category: e.target.value })}
          >
            <option value="">All</option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </div>

        <div className="w-52">
          <label className="form-label" htmlFor="filter-client">
            Client
          </label>
          <SearchableSelect
            id="filter-client"
            options={clients.map((c) => ({ value: c.id, label: c.name }))}
            value={filters.clientId}
            onChange={(clientId) => setFilters({ ...filters, clientId })}
            placeholder="All, type to search…"
          />
        </div>

        {filtersActive && (
          <button
            type="button"
            className="row-action-muted"
            onClick={() => setFilters(EMPTY_FILTERS)}
          >
            Clear filters
          </button>
        )}
      </div>

      {error && (
        <div className="banner-error mb-4" role="alert">
          {error}
        </div>
      )}

      <ReplanningPanel items={replanning} onFix={openEditor} />

      <div aria-busy={loading}>
        {view === 'agenda' && (
          <AgendaView
            days={days}
            byDay={byDay}
            awayByDay={awayByDay}
            resources={resources}
            onOpenDay={setOpenDay}
            onSelectGroup={openGroup}
            nwInfo={nwInfo}
            worksOn={worksOn}
          />
        )}

        {view === 'week' &&
          (phone ? (
            <AgendaView
              days={days}
              byDay={byDay}
              awayByDay={awayByDay}
              resources={resources}
              onOpenDay={setOpenDay}
              onSelectGroup={openGroup}
              nwInfo={nwInfo}
              worksOn={worksOn}
              emptyTitle="Nothing on this week"
            />
          ) : (
            <WeekView
              days={days}
              byDay={byDay}
              awayByDay={awayByDay}
              onOpenDay={setOpenDay}
              onAddOn={(date) => setCreating({ date })}
              onSelectGroup={openGroup}
              nwInfo={nwInfo}
              worksOn={worksOn}
            />
          ))}

        {view === 'month' && (
          <MonthView
            days={monthGrid(anchor)}
            anchorMonth={startOfMonth(anchor)}
            byDay={byDay}
            awayByDay={awayByDay}
            onOpenDay={setOpenDay}
            onAddOn={(date) => setCreating({ date })}
            onSelectGroup={openGroup}
            nwInfo={nwInfo}
            worksOn={worksOn}
          />
        )}

        {view === '3month' && (
          <ThreeMonthView
            months={[0, 1, 2].map((offset) => {
              const monthAnchor = addMonths(startOfMonth(anchor), offset);
              return { anchor: monthAnchor, days: monthGrid(monthAnchor) };
            })}
            byDay={byDay}
            onOpenDay={setOpenDay}
            onSelectGroup={openGroup}
            nwInfo={nwInfo}
            worksOn={worksOn}
          />
        )}

        {view === 'timeline' && (
          <JobTimeline
            bookings={bookings}
            days={days}
            onSelectBooking={openEditor}
            nwInfo={nwInfo}
            worksOn={worksOn}
          />
        )}

        {isResourceTimeline(view) && (
          <ResourceTimeline
            resources={timelineResources}
            bookings={visibleBookings}
            away={away}
            days={days}
            onSelectBooking={openEditor}
            onAddOn={(date, resourceId) => setCreating({ date, resourceId })}
            nwInfo={nwInfo}
            worksOn={worksOn}
          />
        )}
      </div>

      {openDay && (
        <DayPanel
          nwInfo={nwInfo}
          date={openDay}
          bookings={byDay.get(openDay) ?? []}
          away={awayByDay.get(openDay) ?? []}
          resources={resources}
          onClose={() => setOpenDay(null)}
          onEdit={(booking) => {
            setOpenDay(null);
            openEditor(booking);
          }}
          onAdd={() => {
            setCreating({ date: openDay });
            setOpenDay(null);
          }}
          onOpenJob={(jobId) => {
            setOpenJobDay({ jobId, date: openDay });
            setOpenDay(null);
          }}
        />
      )}

      {/* Hidden while an editor is up rather than stacked (ZT's rule): the
          rows re-derive from `bookings`, so when the editor closes and load()
          lands, the panel reappears with the fresh state in place. */}
      {openJobDay && !editingEntries && !editingMaintenance && !creating && (
        <JobDayPanel
          target={openJobDay}
          bookings={bookings}
          worksOn={worksOn}
          nwInfo={nwInfo}
          onClose={() => setOpenJobDay(null)}
          onEdit={(booking) => openEditor(booking)}
          onEditMany={(list) => setEditingEntries(list)}
          onRemove={(booking) => void removeBooking(booking)}
          onAdd={(jobId, date) => setCreating({ date, jobId })}
        />
      )}

      {bookingMaintenance && (
        <MaintenanceModal
          nwInfo={nwInfo}
          resources={resources}
          initial={{
            startDate: bookingMaintenance.date,
            endDate: bookingMaintenance.date,
            ...(bookingMaintenance.resourceId
              ? { resourceId: bookingMaintenance.resourceId }
              : {}),
          }}
          onClose={() => setBookingMaintenance(null)}
          onSaved={() => {
            setBookingMaintenance(null);
            void load();
          }}
        />
      )}

      {/* A maintenance booking opens the maintenance dialog, never the job
          one — it has no job to select and no times to offer, and the two
          forms share nothing but a date range (the MaintenanceModal header
          has the full argument). Before this check every bar opened
          BookingModal, so a maintenance booking had no edit path at all. */}
      {editingMaintenance && (
        <MaintenanceModal
          nwInfo={nwInfo}
          resources={resources}
          booking={editingMaintenance}
          onClose={() => setEditingMaintenance(null)}
          onSaved={onSaved}
          onReleased={() => void load()}
        />
      )}

      {(editingEntries || creating) && (
        <BookingModal
          nwInfo={nwInfo}
          jobs={jobs}
          resources={resources}
          entries={editingEntries ?? undefined}
          initial={
            creating
              ? {
                  startDate: creating.date,
                  endDate: creating.date,
                  ...(creating.resourceId ? { resourceId: creating.resourceId } : {}),
                  ...(creating.jobId ? { jobId: creating.jobId } : {}),
                }
              : undefined
          }
          onClose={() => {
            setEditingEntries(null);
            setCreating(null);
          }}
          onSaved={onSaved}
        />
      )}
    </>
  );
}
